/**
 * Devices on the new job page, with every device action the classic page has
 * (go core, 11/3). Staff did ~1,400 device actions a month on the classic page
 * and the new page could do none of them. This is the classic Results card and
 * the classic dialogs, wired to the same APIs, so nothing behaves differently:
 *
 *  - add a device (pick from the site, or quick-add)   POST /api/jobs/:id/assets(…)
 *  - check against the city's list (backflow)          ReconcileFromListDialog
 *  - edit a device                                     PATCH /api/assets/:id
 *  - take a device off the job                         DELETE /api/jobs/:id/assets/:linkId
 *  - Test / Repair / Replace this visit                PATCH /api/jobs/:id/assets/:assetId
 *  - use the signed paper, record or remove a repair   (office_review.manage, backflow)
 *  - the failed device's fail protocol (send quote …)  folded into its row
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { useJobProof } from "@/components/proof/job-proof";
import { ResultsCard } from "../detail/results-card";
import { AddDevicePicker, EditDeviceModal } from "../detail/device-dialogs";
import { PaperConversionDialog } from "../detail/paper-conversion-dialog";
import { RecordRepairDialog } from "../detail/record-repair-dialog";
import { RemoveRepairDialog } from "../detail/remove-repair-dialog";
import { useJobFailProtocols } from "../detail/fail-protocol-card";
import { ReconcileFromListDialog } from "../../sites/reconcile-from-list";
import { isPostVisit } from "../jobDetailHelpers";
import {
    apiErrorInfo,
    assetKindsForTrade,
    visibleJobAssets,
    type JobAssetRow,
    type JobDetailResponse,
    type TechOption,
} from "../detail/types";

/** Where the office drives the tech's own screens for this job, if anywhere. */
export function fieldWorkflowHrefFor(
    job: Pick<JobDetailResponse["job"], "id" | "kind" | "status" | "trade_code">,
    isOffice: boolean,
): string | null {
    if (!isOffice || job.kind === "vendor_work_order" || ["closed", "cancelled"].includes(job.status)) return null;
    if (job.trade_code === "backflow") return `/field/backflow/${job.id}`;
    if (job.trade_code === "fire_ext") return `/field/fire-safety/${job.id}`;
    return null;
}

/** Why "Add device" is off, in plain words; null when it's on. */
export function addDeviceBlocker(job: Pick<JobDetailResponse["job"], "site_id" | "trade_code">): string | null {
    if (!job.site_id) return "This job has no site";
    if (assetKindsForTrade(job.trade_code ?? null) === null) return "Set a trade on this job before adding devices";
    return null;
}

export function FreshDeviceWork({
    data,
    onUploadReport,
}: {
    data: JobDetailResponse;
    /** Opens this page's paperwork upload (a device's "Upload the report"). */
    onUploadReport?: (() => void) | null;
}) {
    const job = data.job;
    const id = job.id;
    const qc = useQueryClient();
    const { isOffice, can } = useAuth();
    const canConvertPaper = can("office_review.manage");
    const backflow = job.trade_code === "backflow";

    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerTab, setPickerTab] = useState<"existing" | "quick">("existing");
    const [editingAsset, setEditingAsset] = useState<JobAssetRow | null>(null);
    const [reconcileOpen, setReconcileOpen] = useState(false);
    const [paperAsset, setPaperAsset] = useState<JobAssetRow | null>(null);
    const [repairAsset, setRepairAsset] = useState<JobAssetRow | null>(null);
    const [removeRepairAsset, setRemoveRepairAsset] = useState<JobAssetRow | null>(null);

    const failProtocols = useJobFailProtocols(id);
    const jobProof = useJobProof(id);
    const techsQ = useQuery({
        queryKey: ["dispatch", "technicians"],
        queryFn: () => api.get<{ users: TechOption[] }>("/api/dispatch/technicians"),
        staleTime: 5 * 60_000,
        enabled: canConvertPaper && backflow,
    });

    const assets = visibleJobAssets(job.trade_code ?? null, data.assets ?? []);
    const testResults = data.testResults ?? [];
    const repairResults = data.repairResults ?? [];

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["job", id] });
        qc.invalidateQueries({ queryKey: ["jobs"] });
        qc.invalidateQueries({ queryKey: ["dispatch"] });
        qc.invalidateQueries({ queryKey: ["activity-timeline"] });
    };

    const removeAsset = useMutation({
        mutationFn: (linkId: string) => api.delete(`/api/jobs/${id}/assets/${linkId}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", id] });
            toast.success("Device removed from job");
        },
        onError: () => toast.error("Couldn't remove device"),
    });

    const scopeAsset = useMutation({
        mutationFn: ({ assetId, action }: { assetId: string; action: "test" | "repair" | "replace" }) =>
            api.patch(`/api/jobs/${id}/assets/${assetId}`, { requiredAction: action }),
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ["job", id] });
            toast.success(
                vars.action === "replace"
                    ? "Set to Replace. The tech gets the guided replacement steps on this device."
                    : vars.action === "repair"
                      ? "Set to Repair."
                      : "Set to Test.",
            );
        },
        onError: (err: unknown) => {
            const info = apiErrorInfo(err);
            toast.error(info.message ? `Couldn't change it: ${info.message}` : "Couldn't change it");
        },
    });

    const blocker = addDeviceBlocker(job);
    const ripCord = canConvertPaper && backflow;

    return (
        <div id="fresh-devices" data-testid="fresh-device-work" className="scroll-mt-4">
            <ResultsCard
                jobProof={jobProof.proof}
                onUploadReport={onUploadReport ?? null}
                assets={assets}
                testResults={testResults}
                repairResults={repairResults}
                preVisit={!isPostVisit(job.status)}
                tradeHasFamily={assetKindsForTrade(job.trade_code ?? null) !== null}
                addDisabled={blocker !== null}
                addTitle={blocker ?? "Add a device to this job"}
                onAddDevice={() => {
                    setPickerTab("existing");
                    setPickerOpen(true);
                }}
                onReconcile={backflow && job.site_id ? () => setReconcileOpen(true) : null}
                quotedDevicesNudge={!!job.source_estimate_id}
                onEditAsset={setEditingAsset}
                onUnlinkAsset={async (asset) => {
                    const ok = await confirmDialog({
                        message: `Take "${asset.label}" off this job?`,
                        destructive: true,
                        confirmLabel: "Take it off",
                    });
                    if (ok) removeAsset.mutate(asset.link_id);
                }}
                unlinkPending={removeAsset.isPending}
                failProtocols={failProtocols}
                onScopeAsset={
                    isOffice ? (asset, action) => scopeAsset.mutate({ assetId: asset.asset_id, action }) : undefined
                }
                scopePendingAssetId={scopeAsset.isPending ? (scopeAsset.variables?.assetId ?? null) : null}
                onUsePaper={ripCord ? setPaperAsset : undefined}
                onRecordRepair={ripCord ? setRepairAsset : undefined}
                recordRepairForName={(job.assigned_to_name ?? "").trim().split(/\s+/)[0] || null}
                onRemoveRepair={ripCord ? setRemoveRepairAsset : undefined}
                fieldWorkflowHref={fieldWorkflowHrefFor(job, isOffice)}
            />

            {pickerOpen && job.site_id && blocker === null && (
                <AddDevicePicker
                    jobId={id}
                    siteId={job.site_id}
                    tradeCode={job.trade_code ?? null}
                    siteLat={job.site_latitude != null ? Number(job.site_latitude) : null}
                    siteLng={job.site_longitude != null ? Number(job.site_longitude) : null}
                    currentTab={pickerTab}
                    onTabChange={setPickerTab}
                    linkedAssetIds={new Set(assets.map((a) => a.asset_id))}
                    onClose={() => setPickerOpen(false)}
                />
            )}
            {editingAsset && (
                <EditDeviceModal
                    jobId={id}
                    siteId={job.site_id ?? null}
                    asset={editingAsset}
                    onClose={() => setEditingAsset(null)}
                />
            )}
            {reconcileOpen && job.site_id && (
                <ReconcileFromListDialog
                    siteId={job.site_id}
                    jobId={id}
                    open={reconcileOpen}
                    onClose={() => {
                        setReconcileOpen(false);
                        refresh();
                    }}
                />
            )}
            {ripCord && (
                <>
                    <PaperConversionDialog
                        open={paperAsset != null}
                        onClose={() => setPaperAsset(null)}
                        jobId={id}
                        asset={paperAsset}
                        techs={techsQ.data?.users ?? []}
                        defaultTesterId={job.assigned_to ?? null}
                    />
                    <RecordRepairDialog
                        open={repairAsset != null}
                        onClose={() => setRepairAsset(null)}
                        jobId={id}
                        asset={repairAsset}
                        techs={techsQ.data?.users ?? []}
                        defaultTechId={job.assigned_to ?? null}
                        isMckinneySite={(job.water_provider_slug ?? "").trim().toLowerCase() === "mckinney"}
                    />
                    <RemoveRepairDialog
                        open={removeRepairAsset != null}
                        onClose={() => setRemoveRepairAsset(null)}
                        jobId={id}
                        asset={removeRepairAsset}
                        repair={
                            removeRepairAsset
                                ? ([...repairResults]
                                      .reverse()
                                      .find((r) => r.asset_id === removeRepairAsset.asset_id) ?? null)
                                : null
                        }
                    />
                </>
            )}
        </div>
    );
}
